import { destroy2, get2, post2, put2 } from "@/shared/axios";
import { errorToast, getValue, getValuesInArray } from "@/shared/utils/helpers";
import { ZipcodesTableDataRequest } from "@/shared/utils/interfaces";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useBranchId from "./useBranchId";

const useZipCodesHook = (enabled: boolean = true) => {
  const { t } = useTranslation();
  const branch = useBranchId();

  const query = useQuery({
    queryKey: ["zipcodes", branch],
    queryFn: () => get2(`/internal/secure/pos/v1/zipcodes/${branch}`),
    select: (zipcodes: ZipcodesTableDataRequest[]) => {
      return zipcodes.map((zipcode) => {
        const zip: any = Object.keys(zipcode).reduce((acc, cur) => {
          if (
            cur === "delivery_fee" ||
            cur === "order_value" ||
            cur === "area" ||
            cur === "h3_indexes"
          ) {
            acc[cur] = getValuesInArray(zipcode[cur]);
          } else {
            acc[cur] = getValue(zipcode[cur]);
          }
          return acc;
        }, {});

        if (zip.delivery_fee[zip.delivery_fee.length - 1] === "0") {
          zip.delivery_fee.pop();
          zip["free_from"] = zip.order_value.pop();
        }

        return zip;
      }) as ZipcodesTableDataRequest[];
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
  useEffect(() => {
    if (query.isError) {
      errorToast();
    }
  }, [query]);

  const formatForServer = (data: ZipcodesTableDataRequest[]) => {
    return data.map((zipcode) => {
      if (zipcode.free_from) {
        return {
          ...zipcode,
          order_value: [...zipcode.order_value, zipcode.free_from],
          delivery_fee: [...zipcode.delivery_fee, "0"],
          free_from: undefined,
          country: +zipcode.country,
        };
      } else {
        return {
          ...zipcode,
          country: +zipcode.country,
        };
      }
    });
  };

  const create = useMutation({
    mutationFn: (data: any) =>
      post2(`/internal/secure/pos/v1/zipcodes/${branch}`, data),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: (error: AxiosError) => {
      if (error.response.status === 406) {
        toast.error(t("zip_codes.conflictFoundBetweenZipcodes"));
      } else {
        errorToast();
      }
    },
  });

  const destroy = useMutation({
    mutationFn: (id: number) =>
      destroy2(`/internal/secure/pos/v1/zipcodes/${branch}/${id}`),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.delete"), { toastId: "deletingZipCode" });
    },
    onError: () => {
      errorToast();
    },
  });

  const update = useMutation<unknown, unknown, ZipcodesTableDataRequest>({
    mutationFn: ({ item_id, ...req }) =>
      put2(`/internal/secure/pos/v1/zipcodes/${branch}/${item_id}`, req),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.update"), { toastId: "updatingZipCode" });
    },
    onError: (error: AxiosError) => {
      if (error.response.status === 406) {
        toast.error(t("zip_codes.conflictFoundBetweenZipcodes"));
      } else {
        errorToast();
      }
    },
  });

  return { query, create, destroy, update, formatForServer };
};

export default useZipCodesHook;
